import React, { useEffect, useState, useCallback } from "react";
import axios from "utils/axiosConfig";
import { useSelector } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import {
  HiOutlineShieldCheck,
  HiOutlineRefresh,
  HiOutlineLightningBolt,
  HiOutlineDatabase,
  HiOutlineCurrencyDollar,
  HiOutlineLockClosed,
  HiOutlineChartBar,
  HiOutlineClipboardList,
  HiOutlineClock,
  HiOutlineStatusOnline,
  HiOutlineExclamation,
  HiOutlineCheckCircle,
  HiOutlinePlay,
  HiOutlineX,
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
  HiOutlineCube,
  HiOutlineShoppingCart,
  HiOutlineEye,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { useToast } from "../../components/Toast";

// ============ ANALYSIS RESULT COMPONENTS ============

const InventoryAnalysisResults = ({ data }) => {
  const { analysis, alerts } = data;

  return (
    <div className="space-y-8">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
            Total Products
          </p>
          <p className="text-3xl font-black text-white italic">
            {analysis.totalProducts}
          </p>
        </div>
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-emerald-400 font-black uppercase tracking-widest mb-2">
            Well Stocked
          </p>
          <p className="text-3xl font-black text-emerald-400 italic">
            {analysis.wellStocked?.length || 0}
          </p>
        </div>
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-amber-400 font-black uppercase tracking-widest mb-2">
            Low Stock
          </p>
          <p className="text-3xl font-black text-amber-400 italic">
            {analysis.lowStock?.length || 0}
          </p>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-red-400 font-black uppercase tracking-widest mb-2">
            Out of Stock
          </p>
          <p className="text-3xl font-black text-red-400 italic">
            {analysis.outOfStock?.length || 0}
          </p>
        </div>
      </div>

      {/* Inventory Value */}
      <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-2xl p-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-2">
              Total Inventory Value
            </p>
            <p className="text-4xl font-black text-white italic">
              ${analysis.totalInventoryValue?.toLocaleString() || 0}
            </p>
          </div>
          <div className="p-4 bg-purple-500/10 rounded-2xl border border-purple-500/20">
            <HiOutlineCurrencyDollar className="text-purple-400" size={32} />
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {analysis.categoryBreakdown &&
        Object.keys(analysis.categoryBreakdown).length > 0 && (
          <div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
              <HiOutlineCube className="text-cyan-400" size={18} />
              Category Breakdown
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(analysis.categoryBreakdown).map(
                ([category, data]) => (
                  <div
                    key={category}
                    className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-cyan-500/30 transition-all"
                  >
                    <p className="text-xs font-black text-white italic truncate mb-2">
                      {category}
                    </p>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-gray-500 uppercase tracking-wider">
                        {data.count} items
                      </span>
                      <span className="text-cyan-400 font-bold">
                        {data.totalStock} units
                      </span>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}

      {/* Alerts */}
      {alerts && alerts.length > 0 && (
        <div>
          <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
            <HiOutlineExclamationCircle className="text-amber-400" size={18} />
            Stock Alerts ({alerts.length})
          </h4>
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {alerts.map((alert, i) => (
              <div
                key={i}
                className={`flex items-start gap-4 p-5 rounded-xl border ${
                  alert.severity === "high"
                    ? "bg-red-500/5 border-red-500/20"
                    : "bg-amber-500/5 border-amber-500/20"
                }`}
              >
                <div
                  className={`p-2 rounded-lg ${
                    alert.severity === "high"
                      ? "bg-red-500/10"
                      : "bg-amber-500/10"
                  }`}
                >
                  <HiOutlineExclamation
                    className={
                      alert.severity === "high"
                        ? "text-red-400"
                        : "text-amber-400"
                    }
                    size={16}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-white font-bold text-sm">
                      {alert.product}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${
                        alert.severity === "high"
                          ? "bg-red-500/20 text-red-400"
                          : "bg-amber-500/20 text-amber-400"
                      }`}
                    >
                      {alert.severity}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-400">
                    {alert.recommendation}
                  </p>
                  <p className="text-[10px] text-gray-600 mt-1">
                    Current Stock: {alert.currentStock} units
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Low Stock Items */}
      {analysis.lowStock && analysis.lowStock.length > 0 && (
        <div>
          <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
            <HiOutlineTrendingDown className="text-amber-400" size={18} />
            Low Stock Items
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[9px] text-gray-500 uppercase tracking-widest border-b border-white/5">
                  <th className="pb-3 font-black">Product</th>
                  <th className="pb-3 font-black">Category</th>
                  <th className="pb-3 font-black text-right">Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {analysis.lowStock.slice(0, 10).map((item, i) => (
                  <tr key={i} className="text-sm hover:bg-white/[0.02]">
                    <td className="py-4 text-white font-bold">{item.name}</td>
                    <td className="py-4 text-gray-500">{item.category}</td>
                    <td
                      className={`py-4 text-right font-black ${
                        item.stock < 5 ? "text-red-400" : "text-amber-400"
                      }`}
                    >
                      {item.stock}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const SalesAnalysisResults = ({ data }) => {
  const { analysis, recommendations } = data;

  // Get sorted daily revenue for chart
  const dailyData = analysis.dailyRevenue
    ? Object.entries(analysis.dailyRevenue)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .slice(-14)
    : [];
  const maxRevenue = Math.max(...dailyData.map((d) => d[1]), 1);

  return (
    <div className="space-y-8">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-amber-400 font-black uppercase tracking-widest mb-2">
            Total Revenue
          </p>
          <p className="text-3xl font-black text-amber-400 italic">
            ${analysis.totalRevenue?.toLocaleString() || 0}
          </p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
            Total Orders
          </p>
          <p className="text-3xl font-black text-white italic">
            {analysis.totalOrders || 0}
          </p>
        </div>
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-cyan-400 font-black uppercase tracking-widest mb-2">
            Avg Order Value
          </p>
          <p className="text-3xl font-black text-cyan-400 italic">
            ${analysis.averageOrderValue?.toFixed(2) || 0}
          </p>
        </div>
        <div className="bg-purple-500/5 border border-purple-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-purple-400 font-black uppercase tracking-widest mb-2">
            Period
          </p>
          <p className="text-xl font-black text-purple-400 italic">
            {analysis.period}
          </p>
        </div>
      </div>

      {/* Revenue Chart */}
      {dailyData.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-6 flex items-center gap-2">
            <HiOutlineTrendingUp className="text-emerald-400" size={18} />
            Daily Revenue Trend
          </h4>
          <div className="h-48 flex items-end gap-2">
            {dailyData.map(([date, revenue], i) => {
              const height = (revenue / maxRevenue) * 100;
              return (
                <div
                  key={date}
                  className="flex-1 flex flex-col items-center gap-2 group"
                >
                  <div className="relative w-full flex justify-center">
                    <div
                      className="w-full max-w-[40px] bg-gradient-to-t from-amber-500/20 to-amber-500/50 rounded-t-lg transition-all group-hover:from-amber-500/30 group-hover:to-amber-500/70 relative"
                      style={{ height: `${Math.max(height, 4)}%` }}
                    >
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/80 rounded-lg text-[9px] text-white font-bold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        ${revenue.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <span className="text-[8px] text-gray-600 font-bold transform -rotate-45 origin-center whitespace-nowrap">
                    {date.slice(5)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Order Status Breakdown */}
      {analysis.statusBreakdown &&
        Object.keys(analysis.statusBreakdown).length > 0 && (
          <div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
              <HiOutlineShoppingCart className="text-cyan-400" size={18} />
              Order Status
            </h4>
            <div className="flex flex-wrap gap-3">
              {Object.entries(analysis.statusBreakdown).map(
                ([status, count]) => {
                  const colors = {
                    pending: "amber",
                    confirmed: "cyan",
                    processing: "purple",
                    shipped: "blue",
                    delivered: "emerald",
                    cancelled: "red",
                  };
                  const color = colors[status] || "gray";
                  return (
                    <div
                      key={status}
                      className={`flex items-center gap-3 px-5 py-3 bg-${color}-500/10 border border-${color}-500/20 rounded-xl`}
                    >
                      <span
                        className={`text-${color}-400 font-black uppercase text-xs`}
                      >
                        {status}
                      </span>
                      <span
                        className={`px-2 py-0.5 bg-${color}-500/20 rounded-full text-${color}-300 font-black text-sm`}
                      >
                        {count}
                      </span>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}

      {/* Top Products */}
      {analysis.topProducts && Object.keys(analysis.topProducts).length > 0 && (
        <div>
          <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
            <HiOutlineTrendingUp className="text-emerald-400" size={18} />
            Top Performing Products
          </h4>
          <div className="space-y-3">
            {Object.entries(analysis.topProducts)
              .slice(0, 5)
              .map(([code, product], i) => (
                <div
                  key={code}
                  className="flex items-center gap-4 p-5 bg-white/5 border border-white/10 rounded-xl hover:border-emerald-500/30 transition-all"
                >
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-lg font-black text-sm ${
                      i === 0
                        ? "bg-amber-500/20 text-amber-400"
                        : i === 1
                        ? "bg-gray-400/20 text-gray-300"
                        : i === 2
                        ? "bg-orange-600/20 text-orange-400"
                        : "bg-white/5 text-gray-500"
                    }`}
                  >
                    #{i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold truncate">
                      {product.name}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {product.quantity} units sold
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-black">
                      ${product.revenue.toLocaleString()}
                    </p>
                    <p className="text-[9px] text-gray-600 uppercase tracking-wider">
                      Revenue
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-2xl p-8">
          <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
            <HiOutlineLightningBolt className="text-cyan-400" size={18} />
            AI Recommendations
          </h4>
          <div className="space-y-3">
            {recommendations.map((rec, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 bg-white/5 rounded-xl"
              >
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <HiOutlineEye className="text-cyan-400" size={14} />
                </div>
                <div>
                  <p className="text-[10px] text-cyan-400 font-black uppercase tracking-widest mb-1">
                    {rec.type?.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm text-gray-300">{rec.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SecurityAnalysisResults = ({ data }) => {
  const analysis = data;

  // Security score color
  const getScoreColor = (score) => {
    if (score >= 80) return "emerald";
    if (score >= 60) return "amber";
    return "red";
  };
  const scoreColor = getScoreColor(analysis.securityScore);

  return (
    <div className="space-y-8">
      {/* Security Score */}
      <div
        className={`bg-${scoreColor}-500/5 border border-${scoreColor}-500/20 rounded-3xl p-10 relative overflow-hidden`}
      >
        <div
          className={`absolute top-0 right-0 w-64 h-64 bg-${scoreColor}-500/10 rounded-full blur-[80px] pointer-events-none`}
        />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-2">
              Security Score
            </p>
            <div className="flex items-end gap-4">
              <p
                className={`text-7xl font-black text-${scoreColor}-400 italic`}
              >
                {analysis.securityScore}
              </p>
              <span className="text-2xl text-gray-600 font-black mb-3">
                /100
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {analysis.securityScore >= 80
                ? "System security is optimal"
                : analysis.securityScore >= 60
                ? "Some concerns detected"
                : "Critical issues require attention"}
            </p>
          </div>
          <div
            className={`p-6 bg-${scoreColor}-500/10 rounded-3xl border border-${scoreColor}-500/20`}
          >
            <HiOutlineShieldCheck
              className={`text-${scoreColor}-400`}
              size={48}
            />
          </div>
        </div>

        {/* Score Bar */}
        <div className="mt-8 h-3 bg-white/5 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r from-${scoreColor}-600 to-${scoreColor}-400 rounded-full transition-all duration-1000`}
            style={{ width: `${analysis.securityScore}%` }}
          />
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-2">
            Period
          </p>
          <p className="text-xl font-black text-white italic">
            {analysis.period}
          </p>
        </div>
        <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-cyan-400 font-black uppercase tracking-widest mb-2">
            Total Events
          </p>
          <p className="text-3xl font-black text-cyan-400 italic">
            {analysis.totalEvents}
          </p>
        </div>
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
          <p className="text-[10px] text-red-400 font-black uppercase tracking-widest mb-2">
            Errors
          </p>
          <p className="text-3xl font-black text-red-400 italic">
            {analysis.errorCount}
          </p>
        </div>
      </div>

      {/* Suspicious Patterns */}
      {analysis.suspiciousPatterns &&
        analysis.suspiciousPatterns.length > 0 && (
          <div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
              <HiOutlineExclamation className="text-red-400" size={18} />
              Suspicious Patterns Detected
            </h4>
            <div className="space-y-3">
              {analysis.suspiciousPatterns.map((pattern, i) => (
                <div
                  key={i}
                  className={`p-6 rounded-2xl border ${
                    pattern.severity === "high"
                      ? "bg-red-500/5 border-red-500/20"
                      : "bg-amber-500/5 border-amber-500/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div
                        className={`p-3 rounded-xl ${
                          pattern.severity === "high"
                            ? "bg-red-500/10"
                            : "bg-amber-500/10"
                        }`}
                      >
                        <HiOutlineExclamationCircle
                          className={
                            pattern.severity === "high"
                              ? "text-red-400"
                              : "text-amber-400"
                          }
                          size={20}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-white font-black uppercase text-sm">
                            {pattern.type?.replace(/_/g, " ")}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${
                              pattern.severity === "high"
                                ? "bg-red-500/20 text-red-400"
                                : "bg-amber-500/20 text-amber-400"
                            }`}
                          >
                            {pattern.severity}
                          </span>
                        </div>
                        {pattern.message && (
                          <p className="text-sm text-gray-400">
                            {pattern.message}
                          </p>
                        )}
                        {pattern.agentName && (
                          <p className="text-[10px] text-gray-600 mt-2">
                            Agent: {pattern.agentName} • Errors:{" "}
                            {pattern.errorCount}
                          </p>
                        )}
                        {pattern.count && (
                          <p className="text-[10px] text-gray-600 mt-2">
                            Count: {pattern.count}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Agent Activity */}
      {analysis.agentActivity &&
        Object.keys(analysis.agentActivity).length > 0 && (
          <div>
            <h4 className="text-sm font-black text-white uppercase italic tracking-tight mb-4 flex items-center gap-2">
              <HiOutlineShieldCheck className="text-purple-400" size={18} />
              Agent Activity Summary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(analysis.agentActivity).map(([agentId, data]) => (
                <div
                  key={agentId}
                  className="p-5 bg-white/5 border border-white/10 rounded-xl hover:border-purple-500/30 transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-white font-black italic">
                      {data.name}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[8px] font-black ${
                        data.errors > 5
                          ? "bg-red-500/20 text-red-400"
                          : "bg-green-500/20 text-green-400"
                      }`}
                    >
                      {data.errors > 5 ? "Attention Needed" : "Healthy"}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-[10px]">
                    <span className="text-gray-500">
                      <span className="text-white font-bold">
                        {data.events}
                      </span>{" "}
                      events
                    </span>
                    <span
                      className={
                        data.errors > 0 ? "text-red-400" : "text-gray-500"
                      }
                    >
                      <span className="font-bold">{data.errors}</span> errors
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* All Clear Message */}
      {(!analysis.suspiciousPatterns ||
        analysis.suspiciousPatterns.length === 0) &&
        analysis.securityScore >= 80 && (
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-8 flex items-center gap-6">
            <div className="p-4 bg-emerald-500/10 rounded-2xl">
              <HiOutlineCheckCircle className="text-emerald-400" size={32} />
            </div>
            <div>
              <p className="text-lg font-black text-emerald-400 italic uppercase">
                All Systems Nominal
              </p>
              <p className="text-sm text-gray-400 mt-1">
                No suspicious patterns detected. Security protocols operating
                within expected parameters.
              </p>
            </div>
          </div>
        )}
    </div>
  );
};

// ============ MAIN COMPONENT ============

const AgentDashboard = () => {
  const toast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeAnalysis, setActiveAnalysis] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${uri}/api/admin/agents/stats`, {
        headers: { "x-user-email": user?.data?.email },
      });
      setStats(response.data.data);
    } catch (error) {
      toast.error("Failed to sync agent telemetry");
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const fetchLogs = useCallback(
    async (agentId = null) => {
      try {
        const params = new URLSearchParams({ limit: "50" });
        if (agentId) params.append("agentId", agentId);

        const response = await axios.get(
          `${uri}/api/admin/agents/logs?${params}`,
          {
            headers: { "x-user-email": user?.data?.email },
          }
        );
        setLogs(response.data.data);
      } catch (error) {
        console.error("Log fetch failed:", error);
      }
    },
    [user]
  );

  const triggerAnalysis = async (type) => {
    setActiveAnalysis(type);
    try {
      const response = await axios.post(
        `${uri}/api/admin/agents/trigger/${type}`,
        { days: 30 },
        { headers: { "x-user-email": user?.data?.email } }
      );
      setAnalysisResults({ type, data: response.data.data });
      toast.success(
        `${type.charAt(0).toUpperCase() + type.slice(1)} analysis complete`
      );
    } catch (error) {
      toast.error("Analysis trigger failed");
    } finally {
      setActiveAnalysis(null);
    }
  };

  useEffect(() => {
    if (user?.data?.email) {
      fetchStats();
      fetchLogs();
    }
  }, [user, fetchStats, fetchLogs]);

  const agentIcons = {
    agent_sentinel_alpha: <HiOutlineShieldCheck size={24} />,
    agent_nexus_scout: <HiOutlineDatabase size={24} />,
    agent_grid_marshal: <HiOutlineLockClosed size={24} />,
    agent_oracle_finance: <HiOutlineCurrencyDollar size={24} />,
  };

  const agentColors = {
    agent_sentinel_alpha: "cyan",
    agent_nexus_scout: "emerald",
    agent_grid_marshal: "red",
    agent_oracle_finance: "amber",
  };

  const analysisLabels = {
    inventory: {
      label: "Inventory Analysis",
      icon: <HiOutlineDatabase size={20} />,
      color: "emerald",
    },
    sales: {
      label: "Sales Analysis",
      icon: <HiOutlineCurrencyDollar size={20} />,
      color: "amber",
    },
    security: {
      label: "Security Audit",
      icon: <HiOutlineLockClosed size={20} />,
      color: "red",
    },
  };

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              <HiOutlineStatusOnline className="animate-pulse" />
              VR_Staff_Network: Online
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Agent <span className="text-cyan-400">Command</span>
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
              Monitor and orchestrate your AI workforce in real-time.
            </p>
          </div>
          <button
            onClick={() => {
              fetchStats();
              fetchLogs();
            }}
            className="p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all text-cyan-400 group"
          >
            <HiOutlineRefresh
              size={24}
              className={
                loading
                  ? "animate-spin"
                  : "group-hover:rotate-180 transition-transform duration-500"
              }
            />
          </button>
        </header>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 relative overflow-hidden group hover:border-cyan-500/30 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 rounded-full blur-[60px] pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
                    <HiOutlineLightningBolt
                      className="text-cyan-400"
                      size={20}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">
                    Activities
                  </span>
                </div>
                <p className="text-4xl font-black text-white italic">
                  {stats.totalActivities}
                </p>
                <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-2">
                  Last 7 Days
                </p>
              </div>
            </div>

            <div className="bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 relative overflow-hidden group hover:border-amber-500/30 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-[60px] pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
                    <HiOutlineClipboardList
                      className="text-amber-400"
                      size={20}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">
                    Pending
                  </span>
                </div>
                <p className="text-4xl font-black text-white italic">
                  {stats.proposalStats.pending}
                </p>
                <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-2">
                  Awaiting Review
                </p>
              </div>
            </div>

            <div className="bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 relative overflow-hidden group hover:border-green-500/30 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-[60px] pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
                    <HiOutlineCheckCircle
                      className="text-green-400"
                      size={20}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">
                    Approved
                  </span>
                </div>
                <p className="text-4xl font-black text-white italic">
                  {stats.proposalStats.approved}
                </p>
                <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-2">
                  Total Approved
                </p>
              </div>
            </div>

            <div className="bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 relative overflow-hidden group hover:border-red-500/30 transition-all">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-[60px] pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/20">
                    <HiOutlineExclamation className="text-red-400" size={20} />
                  </div>
                  <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">
                    Rejected
                  </span>
                </div>
                <p className="text-4xl font-black text-white italic">
                  {stats.proposalStats.rejected}
                </p>
                <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-2">
                  Total Rejected
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Agent Grid */}
        <section>
          <h2 className="text-2xl font-black text-white uppercase italic tracking-tight mb-8 flex items-center gap-4">
            <HiOutlineShieldCheck className="text-purple-400" />
            VR Staff Directory
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stats?.agents?.map((agent) => {
              const color = agentColors[agent.id] || "cyan";
              return (
                <div
                  key={agent.id}
                  onClick={() => {
                    setSelectedAgent(
                      selectedAgent === agent.id ? null : agent.id
                    );
                    if (selectedAgent !== agent.id) fetchLogs(agent.id);
                    else fetchLogs();
                  }}
                  className={`group relative bg-[#111827]/40 backdrop-blur-xl border rounded-[2rem] p-8 cursor-pointer transition-all duration-500 overflow-hidden ${
                    selectedAgent === agent.id
                      ? `border-${color}-500/50 shadow-[0_0_40px_rgba(0,242,255,0.1)]`
                      : "border-white/10 hover:border-white/20"
                  }`}
                >
                  {/* Status Indicator */}
                  <div
                    className={`absolute top-6 right-6 flex items-center gap-2 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${
                      agent.status === "active"
                        ? "bg-green-500/10 border border-green-500/20 text-green-400"
                        : "bg-gray-500/10 border border-gray-500/20 text-gray-500"
                    }`}
                  >
                    <div
                      className={`w-1.5 h-1.5 rounded-full ${
                        agent.status === "active"
                          ? "bg-green-500 animate-pulse"
                          : "bg-gray-500"
                      }`}
                    />
                    {agent.status}
                  </div>

                  <div className="flex items-start gap-6">
                    {/* Avatar */}
                    <div
                      className={`p-5 bg-${color}-500/10 rounded-2xl border border-${color}-500/20 text-${color}-400`}
                    >
                      {agentIcons[agent.id] || (
                        <HiOutlineShieldCheck size={24} />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="text-xl font-black text-white italic mb-1">
                        {agent.name}
                      </h3>
                      <p
                        className={`text-[10px] text-${color}-400 font-black uppercase tracking-widest mb-4`}
                      >
                        {agent.role}
                      </p>

                      {/* Capabilities */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {agent.capabilities?.slice(0, 3).map((cap) => (
                          <span
                            key={cap}
                            className="px-2 py-1 bg-white/5 border border-white/10 rounded-lg text-[8px] text-gray-400 font-bold uppercase"
                          >
                            {cap.replace(/_/g, " ")}
                          </span>
                        ))}
                        {agent.capabilities?.length > 3 && (
                          <span className="px-2 py-1 text-[8px] text-gray-600 font-bold">
                            +{agent.capabilities.length - 3} more
                          </span>
                        )}
                      </div>

                      {/* Stats Row */}
                      <div className="flex items-center gap-6 text-[10px]">
                        <div className="flex items-center gap-2 text-gray-500">
                          <HiOutlineLightningBolt size={14} />
                          <span className="font-black">
                            {agent.activityCount}
                          </span>
                          <span className="uppercase tracking-wider">
                            events
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <HiOutlineClipboardList size={14} />
                          <span className="font-black">
                            {agent.proposalCount}
                          </span>
                          <span className="uppercase tracking-wider">
                            proposals
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <HiOutlineLockClosed size={14} />
                          <span className="font-black">
                            L{agent.securityLevel}
                          </span>
                        </div>
                      </div>

                      {/* Last Active */}
                      {agent.lastActive && (
                        <div className="mt-4 flex items-center gap-2 text-[9px] text-gray-600">
                          <HiOutlineClock size={12} />
                          <span className="uppercase tracking-widest">
                            Last active:{" "}
                            {new Date(agent.lastActive).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Analysis Triggers */}
        <section>
          <h2 className="text-2xl font-black text-white uppercase italic tracking-tight mb-8 flex items-center gap-4">
            <HiOutlineChartBar className="text-cyan-400" />
            Trigger Analysis
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                type: "inventory",
                label: "Inventory Scan",
                icon: <HiOutlineDatabase size={24} />,
                color: "emerald",
                desc: "Analyze stock levels and generate restock alerts",
              },
              {
                type: "sales",
                label: "Sales Analysis",
                icon: <HiOutlineCurrencyDollar size={24} />,
                color: "amber",
                desc: "Review revenue metrics and pricing opportunities",
              },
              {
                type: "security",
                label: "Security Audit",
                icon: <HiOutlineLockClosed size={24} />,
                color: "red",
                desc: "Scan for anomalies and suspicious patterns",
              },
            ].map(({ type, label, icon, color, desc }) => (
              <button
                key={type}
                onClick={() => triggerAnalysis(type)}
                disabled={activeAnalysis === type}
                className={`group relative bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 text-left hover:border-${color}-500/30 transition-all duration-300 disabled:opacity-50`}
              >
                <div
                  className={`absolute top-0 right-0 w-24 h-24 bg-${color}-500/10 rounded-full blur-[50px] pointer-events-none`}
                />
                <div className="relative z-10">
                  <div
                    className={`inline-flex p-4 bg-${color}-500/10 rounded-2xl border border-${color}-500/20 text-${color}-400 mb-6`}
                  >
                    {activeAnalysis === type ? (
                      <HiOutlineRefresh className="animate-spin" size={24} />
                    ) : (
                      icon
                    )}
                  </div>
                  <h3 className="text-lg font-black text-white italic mb-2 uppercase">
                    {label}
                  </h3>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                    {desc}
                  </p>

                  <div
                    className={`mt-6 flex items-center gap-2 text-${color}-400 text-[10px] font-black uppercase tracking-widest group-hover:translate-x-1 transition-transform`}
                  >
                    <HiOutlinePlay size={14} />
                    <span>
                      {activeAnalysis === type ? "Running..." : "Execute"}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Analysis Results - Enhanced UI */}
        {analysisResults && (
          <section className="bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-10 relative overflow-hidden">
            {/* Background Glow */}
            <div
              className={`absolute top-0 right-0 w-96 h-96 bg-${
                analysisLabels[analysisResults.type]?.color
              }-500/5 rounded-full blur-[100px] pointer-events-none`}
            />

            {/* Header */}
            <div className="relative z-10 flex items-center justify-between mb-10">
              <div className="flex items-center gap-4">
                <div
                  className={`p-4 bg-${
                    analysisLabels[analysisResults.type]?.color
                  }-500/10 rounded-2xl border border-${
                    analysisLabels[analysisResults.type]?.color
                  }-500/20 text-${
                    analysisLabels[analysisResults.type]?.color
                  }-400`}
                >
                  {analysisLabels[analysisResults.type]?.icon}
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase italic tracking-tight">
                    {analysisLabels[analysisResults.type]?.label}
                  </h2>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                    Generated: {new Date().toLocaleString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAnalysisResults(null)}
                className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all text-gray-400 hover:text-white"
              >
                <HiOutlineX size={20} />
              </button>
            </div>

            {/* Render appropriate component based on analysis type */}
            <div className="relative z-10">
              {analysisResults.type === "inventory" && (
                <InventoryAnalysisResults data={analysisResults.data} />
              )}
              {analysisResults.type === "sales" && (
                <SalesAnalysisResults data={analysisResults.data} />
              )}
              {analysisResults.type === "security" && (
                <SecurityAnalysisResults data={analysisResults.data} />
              )}
            </div>
          </section>
        )}

        {/* Activity Logs */}
        <section>
          <h2 className="text-2xl font-black text-white uppercase italic tracking-tight mb-8 flex items-center gap-4">
            <HiOutlineClock className="text-purple-400" />
            Activity Stream
            {selectedAgent && (
              <span className="text-sm text-purple-400 normal-case not-italic">
                (Filtered:{" "}
                {stats?.agents?.find((a) => a.id === selectedAgent)?.name})
              </span>
            )}
          </h2>

          <div className="bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-[2.5rem] overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto">
              {logs.length === 0 ? (
                <div className="p-12 text-center">
                  <p className="text-gray-500 font-black uppercase tracking-widest text-xs italic">
                    No activity logs detected
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {logs.map((log, i) => (
                    <div
                      key={log._id || i}
                      className="p-6 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`p-3 rounded-xl bg-${
                            agentColors[log.agentId] || "cyan"
                          }-500/10 border border-${
                            agentColors[log.agentId] || "cyan"
                          }-500/20`}
                        >
                          {agentIcons[log.agentId] || (
                            <HiOutlineShieldCheck
                              className="text-cyan-400"
                              size={16}
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-4 mb-2">
                            <span className="font-black text-white text-sm italic">
                              {log.agentName}
                            </span>
                            <span className="text-[9px] text-gray-600 font-bold uppercase tracking-widest shrink-0">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span
                              className={`px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-wider ${
                                log.activityType === "error_occurred"
                                  ? "bg-red-500/10 text-red-400 border border-red-500/20"
                                  : log.activityType === "proposal_submitted"
                                  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                                  : "bg-white/5 text-gray-400 border border-white/10"
                              }`}
                            >
                              {log.activityType?.replace(/_/g, " ")}
                            </span>
                            {log.data?.endpoint && (
                              <span className="text-[10px] text-gray-500 font-mono truncate">
                                {log.data.endpoint}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default AgentDashboard;
